import React, {useEffect} from 'react';
import {motion, useAnimation, Variants} from 'framer-motion';
import {config} from '../environment';

const phoneWidth = 267; // Width of the phone
const phoneHeight = 546; // Height of the phone
const pathLength = phoneHeight * 2 + phoneWidth * 2; // Calculate the total length of the path

const phoneVariants: Variants = {
  initial: {
    strokeDasharray: `${pathLength} ${pathLength}`,
    strokeDashoffset: pathLength,
  },
  animate: {
    strokeDashoffset: 0,
    transition: {
      duration: 2, // Animation duration in seconds
      ease: 'easeInOut', // Animation easing
    },
  },
};

const outerCircleVariants: Variants = {
  initial: {
    opacity: 0,
    strokeDashoffset: '2 1',
  },
  animate: {
    opacity: 1,
    strokeDashoffset: 0,
    transition: {
      duration: 1.5,
      delay: 1.5,
      ease: 'easeInOut',
    },
  },
};

const barVariants: Variants = {
  initial: {
    opacity: 0,
    scale: 0,
  },
  animate: {
    opacity: 1,
    scale: -1,
    transition: {
      duration: 0.3,
      delay: 1.7,
      ease: 'easeInOut',
    },
  },
};

const PhoneWallboxLanding = (props: {width?: number; height?: number}) => {
  const ref = React.useRef<SVGSVGElement>(null);
  const phone = useAnimation();
  const circleOuter = useAnimation();
  const bar = useAnimation();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        // Start the animation if the element is in the viewport
        if (entry.isIntersecting) {
          phone.start('animate');
          circleOuter.start('animate');
          bar.start('animate');
        }
      },
      {
        root: null,
        rootMargin: '0px',
        threshold: 0.1,
      },
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => {
      if (ref.current) {
        observer.unobserve(ref.current);
      }
    };
  }, [bar, circleOuter, phone]);

  return (
    <svg
      ref={ref}
      width={props.width || 348}
      height={props.height || 549}
      viewBox="0 0 348 549"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <motion.rect
        x="1.5"
        y="1.5"
        width={phoneWidth}
        height={phoneHeight}
        rx="25.5"
        stroke="white"
        strokeWidth="3"
        initial="initial"
        animate={phone}
        variants={phoneVariants}
      />
      <motion.path
        d="M101.25 27H168.75"
        stroke="white"
        strokeWidth="8"
        strokeLinecap="round"
        initial="initial"
        animate={bar}
        variants={barVariants}
      />
      <mask
        id="mask1_1297_10317"
        maskUnits="userSpaceOnUse"
        x="267"
        y="0"
        width="81"
        height="549"
      >
        <rect x="267" width="81" height="549" fill="#D9D9D9" />
      </mask>
      <g mask="url(#mask1_1297_10317)">
        <motion.circle
          cx="202"
          cy="254"
          r="120"
          stroke="white"
          strokeWidth="2"
          strokeLinecap="round"
          strokeDasharray="1 6"
          initial="initial"
          animate={circleOuter}
          variants={outerCircleVariants}
        />
      </g>

      <motion.image
        xlinkHref={`${config.AMPOL_AMP_CHARGE_ASSETS_BASE_URL}vw-amp-charge-partial.png`}
        x="34"
        y="70"
        height="446"
        width="233"
        initial="initial"
        animate={circleOuter}
        variants={outerCircleVariants}
      />
    </svg>
  );
};

export default PhoneWallboxLanding;
